import React from "react";
import {
  Grid,
  InputLabel,
  Select,
  MenuItem,
  FormControl,
  Tooltip,
  TextField,
  Backdrop,
  Typography
} from "@material-ui/core";

import WithAuth from "../containers/WithAuth";
import CustomClipFeedSelector from "./CustomClipFeedSelector";
import { inject, observer } from "mobx-react";
import IconButton from "@material-ui/core/IconButton";
import CircularProgress from "@material-ui/core/CircularProgress";
import {
  ArrowBack,
  ArrowForward,
  Forward5,
  History,
  Replay5,
  Save
} from "@material-ui/icons";
import HLSVideo from "../components/HLSVideo";
import Button from "@material-ui/core/Button";
import { withStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import CustomClipCreatedToast from "./CustomClipCreatedToast";
import {
  formatPlayerTimeWithOffset,
  formatPlayerTime
} from "../utils/videoEditorUtils";
import AudioOffsetInput from "../components/AudioOffsetInput";
import { reaction } from "mobx";

const SavingProgress = withStyles({
  root: {
    width: "1.5rem !important",
    height: "1.5rem !important",
    top: "6px",
    position: "relative",
    margin: "0 12px"
  }
})(CircularProgress);

const styles = theme => ({
  hideNumberSpinners: {
    // For Chrome, Safari, Edge, Opera
    "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
      "-webkit-appearance": "none",
      margin: 0
    },
    // For Firefox
    "& input[type=number]": {
      "-moz-appearance": "textfield"
    }
  }
});

const ASSET_NAME = "assetName";
const AUDIO_TRACK = "audioTrack";
const AUDIO_OFFSET = "audioOffset";

class CustomClipVideoEditor extends React.Component {
  constructor(props) {
    super(props);

    this.syncPlayStart = this.syncPlayStart.bind(this);
    this.syncPlayEnd = this.syncPlayEnd.bind(this);
    this.loadPlayStart = this.loadPlayStart.bind(this);
    this.loadPlayEnd = this.loadPlayEnd.bind(this);
    this.reset = this.reset.bind(this);
  }

  setUrlParam(paramKey, paramValue) {
    const params = new URLSearchParams(window.location.search);
    params.set(paramKey, paramValue);
    const newURL = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState({}, "", newURL);
  }

  componentDidMount() {
    const { hlsPlayerStore, createCustomClipStore } = this.props;
    this.audioTrackDisposer = reaction(
      () => hlsPlayerStore.selectedAudioTrack,
      selectedTrack => {
        const params = new URLSearchParams(window.location.search);
        const currentTrack = params.get(AUDIO_TRACK);

        if (!currentTrack || currentTrack !== String(selectedTrack)) {
          this.setUrlParam(AUDIO_TRACK, selectedTrack);
        }
      }
    );

    this.assetNameDisposer = reaction(
      () => createCustomClipStore.selectedAsset,
      selectedAsset => {
        const { hlsPlayerStore } = this.props;

        if (!selectedAsset || !selectedAsset.name) {
          return;
        }
        const params = new URLSearchParams(window.location.search);
        const currentAssetName = params.get(ASSET_NAME);

        if (!currentAssetName || currentAssetName !== selectedAsset.name) {
          this.setUrlParam(ASSET_NAME, selectedAsset.name);

          // Reset the audio track index and offset when a user switches feeds
          // the audio tracks may not have the same delay or the mappings between the
          // tracks could be different and that would cause the ui to behave unexpectedly
          hlsPlayerStore.setAudioTrackByIndex(0);
          hlsPlayerStore.setAudioOffset(0);
        }
      }
    );

    this.audioOffsetDisposer = reaction(
      () => hlsPlayerStore.audioOffset,
      audioOffset => {
        const params = new URLSearchParams(window.location.search);
        const currentAudioOffset = params.get(AUDIO_OFFSET);

        if (!currentAudioOffset || currentAudioOffset !== audioOffset) {
          this.setUrlParam(AUDIO_OFFSET, audioOffset);
        }
      }
    );

    this.loadFromUrl();
  }

  componentWillUnmount() {
    if (this.audioOffsetDisposer) {
      this.audioOffsetDisposer();
    }

    if (this.assetNameDisposer) {
      this.assetNameDisposer();
    }

    if (this.audioTrackDisposer) {
      this.audioTrackDisposer();
    }
  }

  loadFromUrl() {
    const { hlsPlayerStore } = this.props;
    const params = new URLSearchParams(window.location.search);

    const audioTrack = params.get(AUDIO_TRACK);
    const audioOffset = params.get(AUDIO_OFFSET);

    if (audioTrack !== null) {
      hlsPlayerStore.setAudioTrackByIndex(parseInt(audioTrack, 10));
    } else {
      hlsPlayerStore.setAudioTrackByIndex(0);
      this.setUrlParam(AUDIO_TRACK, 0);
    }

    if (audioOffset !== null) {
      hlsPlayerStore.setAudioOffset(parseFloat(audioOffset).toFixed(1));
    } else {
      hlsPlayerStore.setAudioOffset(0);
      this.setUrlParam(AUDIO_OFFSET, 0);
    }
  }

  reset() {
    const { createCustomClipStore, hlsPlayerStore } = this.props;
    createCustomClipStore.resetClipTiming();
    hlsPlayerStore.setCurrentTime(createCustomClipStore.clipStartOffsetSeconds);
  }

  loadPlayStart() {
    const { createCustomClipStore, hlsPlayerStore } = this.props;

    hlsPlayerStore.setCurrentTime(createCustomClipStore.clipStartOffsetSeconds);
  }

  syncPlayStart() {
    const { createCustomClipStore, hlsPlayerStore } = this.props;
    const videoTime = hlsPlayerStore.getCurrentTime();
    createCustomClipStore.adjustClipStart(videoTime);

    if (videoTime > createCustomClipStore.clipEndOffsetSeconds) {
      createCustomClipStore.adjustClipEnd(videoTime);
    }
  }

  loadPlayEnd() {
    const { createCustomClipStore, hlsPlayerStore } = this.props;
    hlsPlayerStore.setCurrentTime(createCustomClipStore.clipEndOffsetSeconds);
  }

  syncPlayEnd() {
    const { createCustomClipStore, hlsPlayerStore } = this.props;
    const videoTime = hlsPlayerStore.getCurrentTime();
    createCustomClipStore.adjustClipEnd(videoTime);

    if (videoTime < createCustomClipStore.clipStartOffsetSeconds) {
      createCustomClipStore.adjustClipStart(videoTime);
    }
  }

  adjustVideo(seconds) {
    const { hlsPlayerStore } = this.props;
    const videoTime = hlsPlayerStore.getCurrentTime();
    hlsPlayerStore.setCurrentTime(Math.max(videoTime + seconds, 0));
  }

  handleAudioOffsetChanged = value => {
    const { hlsPlayerStore } = this.props;
    hlsPlayerStore.setAudioOffset(value);
  };

  saveClip = async () => {
    const { hlsPlayerStore, createCustomClipStore } = this.props;
    const audioTrack = hlsPlayerStore.hlsAudioTrack;
    const audioOffset = hlsPlayerStore.audioOffset;

    const renditionName =
      audioTrack && audioTrack.attrs ? audioTrack.attrs.NAME : "";

    await createCustomClipStore
      .saveClip(renditionName, audioOffset)
      .then(res => {
        if (res) {
          toast.success(<CustomClipCreatedToast />, {
            position: "top-right",
            autoClose: 30000
          });
        }
      })
      .catch(() => {
        toast.error("Failed to save custom video", {
          position: "top-right",
          autoClose: 3000
        });
      });
  };

  render() {
    const { authStore, createCustomClipStore, hlsPlayerStore } = this.props;

    if (createCustomClipStore.fetching) {
      return (
        <Backdrop
          style={{
            color: "#fff",
            zIndex: 3,
            position: "relative",
            height: "75vh"
          }}
          open={true}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      );
    }

    const currentTime = hlsPlayerStore.currentTime;
    const audioOffset = hlsPlayerStore.audioOffset;

    return (
      <React.Fragment>
        <FormControl
          style={{ float: "left", marginBottom: "1rem", width: "50%" }}
        >
          <TextField
            id="clip-description"
            label="Description"
            variant="standard"
            required
            error={createCustomClipStore.descriptionError}
            helperText={
              createCustomClipStore.descriptionError
                ? "Description is required"
                : ""
            }
            placeholder={"Some description about the clip"}
            value={createCustomClipStore.clipDescription}
            onChange={e =>
              createCustomClipStore.setClipDescription(e.target.value)
            }
          />
        </FormControl>
        <CustomClipFeedSelector
          createCustomClipStore={createCustomClipStore}
          onFeedChange={() => this.reset()}
        />

        <HLSVideo
          key={createCustomClipStore.selectedAsset.playbackUrl}
          src={
            createCustomClipStore.selectedAsset
              ? createCustomClipStore.selectedAsset.playbackUrl
              : ""
          }
          assetSource={
            createCustomClipStore.selectedAsset
              ? createCustomClipStore.selectedAsset.source
              : ""
          }
          token={authStore.accessToken}
          startInSeconds={createCustomClipStore.clipStartOffsetSeconds}
          endInSeconds={createCustomClipStore.clipEndOffsetSeconds}
        />

        <Grid
          container
          direction="row"
          justifyContent="center"
          alignItems="center"
          spacing={3}
          style={{ marginTop: "1rem" }}
        >
          <Grid item>
            <Tooltip title="Set start time from video">
              <Button variant="outlined" onClick={e => this.syncPlayStart()}>
                Set Start
                <br />
                {createCustomClipStore.clipStartOffset}
              </Button>
            </Tooltip>
          </Grid>

          <Grid item style={{ textAlign: "center" }}>
            <TooltipIconButton
              tooltip={"Show video start frame"}
              handler={e => this.loadPlayStart()}
              icon={ArrowBack}
            />
            <TooltipIconButton
              tooltip={"Go back 5 seconds"}
              handler={e => this.adjustVideo(-5)}
              icon={Replay5}
            />
            <TooltipIconButton
              tooltip={"Restore timing to original clip"}
              handler={e => this.reset()}
              icon={History}
            />
            {createCustomClipStore.saving ? (
              <SavingProgress />
            ) : (
              <TooltipIconButton
                tooltip={"Save custom clip with new start and end time"}
                handler={() => this.saveClip()}
                icon={Save}
              />
            )}
            <TooltipIconButton
              tooltip={"Go forward 5 seconds"}
              handler={e => this.adjustVideo(5)}
              icon={Forward5}
            />
            <TooltipIconButton
              tooltip={"Show video end frame"}
              handler={e => this.loadPlayEnd()}
              icon={ArrowForward}
            />
          </Grid>

          <Grid item style={{ textAlign: "right" }}>
            <Tooltip title="Set end time from video">
              <Button variant="outlined" onClick={e => this.syncPlayEnd()}>
                Set End
                <br />
                {createCustomClipStore.clipEndOffset}
              </Button>
            </Tooltip>
          </Grid>

          {hlsPlayerStore.selectedAudioTrack !== -1 &&
            hlsPlayerStore.audioTracks &&
            hlsPlayerStore.audioTracks.length > 0 &&
            createCustomClipStore.selectedAsset &&
            createCustomClipStore.selectedAsset.audioTracks &&
            createCustomClipStore.selectedAsset.audioTracks.length > 0 && (
              <Grid item>
                <Grid
                  container
                  direction="row"
                  justifyContent="center"
                  alignItems="center"
                  spacing={3}
                >
                  <Grid item>
                    <FormControl style={{ minWidth: 120 }}>
                      <InputLabel id="dm5-audio-track">Audio Track</InputLabel>
                      <Select
                        labelId="dm5-audio-track-label"
                        id="dm5-audio-feed-select"
                        label="Audio Feed"
                        value={hlsPlayerStore.selectedAudioTrack}
                        onChange={e =>
                          hlsPlayerStore.setAudioTrackByIndex(e.target.value)
                        }
                      >
                        {hlsPlayerStore.audioTracks
                          .filter(item =>
                            createCustomClipStore.selectedAsset.audioTracks.some(
                              track => track.renditionName === item.attrs.NAME
                            )
                          )
                          .map(({ attrs: item, value }) => (
                            <MenuItem key={item.NAME} value={value}>
                              {item.NAME}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item>
                    <Grid container direction="column">
                      <Grid item>
                        <Grid container direction="column">
                          <Typography variant="caption">Video</Typography>
                          <Typography variant="caption">
                            {formatPlayerTime(currentTime)}
                          </Typography>
                        </Grid>
                      </Grid>
                      <Grid item>
                        <Grid container direction="column">
                          <Typography variant="caption">Audio</Typography>
                          <Typography variant="caption">
                            {formatPlayerTimeWithOffset(
                              currentTime,
                              audioOffset
                            )}
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item>
                    <Grid
                      container
                      direction="row"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <AudioOffsetInput
                        disabled={hlsPlayerStore.isSelectedDefaultAudioTrack}
                        disabledTooltip="Audio offset adjustment is not available for the default audio track."
                        defaultAudioOffset={hlsPlayerStore.audioOffset}
                        onAudioOffsetChanged={this.handleAudioOffsetChanged}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            )}
        </Grid>
      </React.Fragment>
    );
  }
}

class TooltipIconButton extends React.Component {
  render() {
    const { tooltip, handler, icon } = this.props;
    const Icon = icon;
    return (
      <Tooltip title={tooltip}>
        <IconButton onClick={handler}>
          <Icon style={{ fontSize: "2rem" }} />
        </IconButton>
      </Tooltip>
    );
  }
}

export default WithAuth(
  inject(
    "authStore",
    "createCustomClipStore",
    "hlsPlayerStore"
  )(withStyles(styles)(observer(CustomClipVideoEditor)))
);



// WEBPACK FOOTER //
// ./src/components/CustomClipVideoEditor.js